import { put, select, takeLatest } from 'redux-saga/effects';
import { TaskId } from 'api/models';
import { Task, TaskAPI } from 'api/tasks';
import { FetchStatus, StoreActionProps } from 'data/core';
import { queryClient } from 'queries';
import { getProjectTasksQueryKey } from 'queries/common';
import { invalidateTaskQuery } from 'utils/invalidate-task-query';
import {
  saveTaskDetailSaga,
  UpdateTaskActionPayload,
  updateTaskSagaAction,
  updateTaskDatesSagaAction,
  updateTaskTimelineSagaAction,
  updateTaskRecordWithLatestDataSagaAction,
} from '../actions';
import { selectTask } from '../selectors';
import { saveTasks } from '../slice';

function* updateTaskSaga(
  action: StoreActionProps<UpdateTaskActionPayload>,
): Generator {
  const { payload } = action;

  if (payload) {
    const { taskId, projectId, data, options } = payload;

    const oldTaskState = (yield select(
      selectTask(TaskId(projectId, taskId)),
    )) as Task | undefined;

    try {
      const updatedTask = (yield TaskAPI.updateTask(
        projectId,
        taskId,
        data,
        options?.isSync,
        options?.actor,
      )) as Task;

      yield put(
        updateTaskRecordWithLatestDataSagaAction({
          taskId,
          data: updatedTask,
          projectId,
        }),
      );

      const newTask =
        options?.refetchGetData &&
        ((yield TaskAPI.getTask(projectId, taskId)) as Task);

      if (options?.resolveCallback) {
        options.resolveCallback(newTask || updatedTask);
      }

      if (!options?.skipStoreUpdate) {
        yield put(saveTaskDetailSaga(newTask || updatedTask));
      }

      if (!options?.skipRefetch) {
        yield invalidateTaskQuery(taskId);
        queryClient.refetchQueries(
          getProjectTasksQueryKey(['project_id', projectId]),
        );
      }
      if (updatedTask && options?.toggleUpdating) {
        options.toggleUpdating(false);
      }
    } catch {
      if (oldTaskState) {
        if (options?.rejectCallback) {
          options.rejectCallback(oldTaskState);
        }
        yield put(
          saveTasks({ data: [oldTaskState], fetchStatus: FetchStatus.Loaded }),
        );
      }
    }
  }
}

function* updateTaskDatesSaga(
  action: StoreActionProps<UpdateTaskActionPayload>,
): Generator {
  const { payload } = action;

  if (payload) {
    const { data, options } = payload;
    const { taskId, projectId } = data;

    const oldTaskState = (yield select(
      selectTask(TaskId(projectId, taskId)),
    )) as Task | undefined;

    try {
      const updatedTask = (yield TaskAPI.updateTask(
        projectId,
        taskId,
        data,
      )) as Task;

      if (!options?.skipStoreUpdate) {
        yield put(saveTaskDetailSaga(updatedTask));
      }
    } catch {
      if (oldTaskState) {
        yield put(
          saveTasks({ data: [oldTaskState], fetchStatus: FetchStatus.Loaded }),
        );
      }
    }
  }
}

export function* watchUpdateTaskSaga(): Generator {
  yield takeLatest(updateTaskSagaAction.type, updateTaskSaga);
  yield takeLatest(updateTaskTimelineSagaAction.type, updateTaskSaga);
}

export function* watchUpdateTaskDatesSaga(): Generator {
  yield takeLatest(updateTaskDatesSagaAction.type, updateTaskDatesSaga);
}
